import { EventHelper, CustomEventDispatcher } from "@utils/helpers/event-helper"
import { relativePositionToElement } from "@utils/helpers/relative-element-position"
import type { ScrollLock } from "./scroll-locker"
import type { SimpleEventUnsubscriber } from "@utils/helpers/simple-event-dispatcher"

const HOLD_MILLISECONDS = 600
const DRAG_DISTANCE_THRESHOLD = 20
export const MULTI_TAP_DISTANCE_MIN = 20
export const MULTI_TAP_DELAY = 300

export enum MultiTap {
    SINGLE,
    DOUBLE,
    TRIPLE,
}

type SelectionEvent = CustomEvent<{ x: number; y: number }>
type SelectionTapEvent = CustomEvent<{
    x: number
    y: number
    tap: MultiTap
}>

export interface SelectionLogicEventMap {
    tap: SelectionTapEvent
    grab: SelectionEvent
    dragstart: SelectionEvent
    dragmove: SelectionEvent
    dragend: SelectionEvent
}

/**
 * If the target is a child of this class, then the events
 * will be deemed "irrelevant".
 */
export const blockListenerPropagation = "highlighterlistener--stop-propagation"

/**
 * Should be set to the parent scrollable container, allowing us to disable scrolling
 * when dragging
 */
export const selectionLogicScrollableParent = "highlighterlistener--scrollable-parent"

/**
 * Responsible for controlling selection and ranges
 */
export class SelectionLogic extends CustomEventDispatcher<SelectionLogicEventMap> {
    readonly eventHelper = new EventHelper<WindowEventMap>(window)
    /**
     * ID that's cancelled if we let our finger go up.
     * This is what makes it a tap instead of a long press.
     */
    #tapTimeoutID = 0
    /**
     * Used to determine if we're dragging right off the bat.
     */
    #distanceFromStart = 0

    /**
     * This we toggle on when we start dragging, and vice versa
     */
    #dragging = false

    /**
     * This we toggle on when there is {@link DRAG_DISTANCE_THRESHOLD} ready to check.
     * @prop {number} x and
     * @prop {number} y mark the start of the drag so we can calculate distances
     */
    #pendingDrag: { x: number; y: number } | null = null

    /**
     * Is there pointer down or not
     */
    #pointerDown = false

    /**
     * Responsible for h
     */
    #multitapHandler = new SelectionMultiTapHandler()

    /**
     * This tracks whether the pointer action should actually be applied
     * here. This is useful for example in search results where we only want
     * to work with our own things.
     */
    #relevantPointerAction = true

    #scrollLockSymbol = Symbol()
    #scrollLockUnsubscriber : SimpleEventUnsubscriber

    constructor(public readonly offsetParent: HTMLElement, public readonly scrollLock: ScrollLock) {
        super()

        this.eventHelper.registerListener("pointerdown", this.#onPointerDown.bind(this))
        this.eventHelper.registerListener("pointercancel", this.#onPointerUp.bind(this))
        this.eventHelper.registerListener("pointerup", this.#onPointerUp.bind(this))
        this.eventHelper.registerListener("pointermove", this.#onPointerMove.bind(this))
        
        this.#scrollLockUnsubscriber = scrollLock.subscribe("onscroll", () => this.#handleScroll())
    }

    /**
     * -------------------------------------------------------------------------
     * 📡 Event Dispatching
     * -------------------------------------------------------------------------
     */

    /**
     * Dispatch a tap event. This is used to indicate a single tap.
     * @param x
     * @param y
     */
    #dispatchTapEvent(x: number, y: number, tap: MultiTap) {
        const event = new CustomEvent("tap", { detail: { x, y, tap } })
        this.dispatchEvent(event)
    }

    // #dispatchGrabWordEvent(x: number, y: number) {
    //     const event = new CustomEvent("grab", { detail: { x, y } })
    //     this.dispatchEvent(event)
    // }

    #draggingStart(x: number, y: number) {
        this.#dragging = true
        const event = new CustomEvent("dragstart", { detail: { x, y } })
        this.dispatchEvent(event)
    }

    #draggingMove(x: number, y: number) {
        const event = new CustomEvent("dragmove", { detail: { x, y } })
        this.dispatchEvent(event)
    }

    #draggingEnd(x: number, y: number) {
        this.#dragging = false
        const event = new CustomEvent("dragend", { detail: { x, y } })
        this.dispatchEvent(event)
    }

    /**
     * -------------------------------------------------------------------------
     * 🖱️ 👆 Pointer Events
     * -------------------------------------------------------------------------
     */

    #onPointerDown(e: WindowEventMap["pointerdown"]) {
        if (!e.isPrimary) return
        const [x, y] = this.#relativePosition(e.clientX, e.clientY)

        // Process to set if relevant or not - basically checks to see
        // if in the bounds of the offsetParent
        this.#relevantPointerAction = this.#checkIfRelevant(e)
        if (!this.#relevantPointerAction) {
            return
        }

        this.#pointerDown = true

        if (e.pointerType == "mouse") {
            // We don't want a long press for a mouse, it's either a tap or it becomes a drag
            this.#pendingDrag = { x, y }
        } else {
            // "touch" or "pen"
            // If it's a touch, we want to see if it's a tap
            this.#tapTimeoutID = setTimeout(() => {
                this.scrollLock.lock(this.#scrollLockSymbol)
                this.#draggingStart(x, y)
                this.#dragging = true
                this.#tapTimeoutID = 0
            }, HOLD_MILLISECONDS) as unknown as number
        }
    }

    #onPointerUp(e: WindowEventMap["pointerup"]) {
        if (!e.isPrimary || !this.#relevantPointerAction || !this.#pointerDown) return
        this.#pointerDown = false
        const [x, y] = this.#relativePosition(e.clientX, e.clientY)

        if (e.pointerType == "mouse") {
            if (this.#dragging) {
                this.#draggingEnd(x, y)
            } else {
                this.#pendingDrag = null
                const { tap } = this.#multitapHandler.processMultiTapEvent(x, y)
                this.#dispatchTapEvent(x, y, tap)
            }
        } else {
            // Tap & touch
            if (this.#dragging) {
                this.scrollLock.unlock(this.#scrollLockSymbol)
                this.#draggingEnd(x, y)
            } else {
                clearTimeout(this.#tapTimeoutID)
                const { tap } = this.#multitapHandler.processMultiTapEvent(x, y)
                this.#dispatchTapEvent(x, y, tap)
            }
        }
    }

    #onPointerMove(e: WindowEventMap["pointermove"]) {
        if (!this.#pointerDown) return
        if (!e.isPrimary || !this.#relevantPointerAction) return
        const [x, y] = this.#relativePosition(e.clientX, e.clientY)

        if (e.pointerType == "mouse") {
            if (this.#dragging) {
                this.#draggingMove(x, y)
            } else if (this.#pendingDrag) {
                const distance = Math.hypot(this.#pendingDrag.x - x, this.#pendingDrag.y - y)
                if (distance > DRAG_DISTANCE_THRESHOLD) {
                    this.#draggingStart(this.#pendingDrag.x, this.#pendingDrag.y)
                    this.#pendingDrag = null
                }
            }
        } else {
            // Touch or tap
            if (this.#dragging) {
                this.#draggingMove(x, y)
            }
        }
    }

    /**
     * -------------------------------------------------------------------------
     * 📡 External Listeners
     * -------------------------------------------------------------------------
     */

    /**
     * Picks up the scroll event from {@link ScrollLock}, which essentially
     * lets us cancel the grab word event (long press).
     * 
     * Only handles mobile scrolling via `ontouchmove`.
     */
    #handleScroll() {
        console.log("scrolling...")
        if (this.#tapTimeoutID !== 0) clearTimeout(this.#tapTimeoutID)
        this.#dragging = false
        this.#pendingDrag = null
        this.#pointerDown = false
    }

    /**
     * -------------------------------------------------------------------------
     * 🔧 Utilities
     * -------------------------------------------------------------------------
     */

    /**
     * We only want to actually process pointer events if they
     * started in our offsetParent.
     */
    #checkIfRelevant(e: PointerEvent) {
        let parentElement = e.target as HTMLElement | null

        let inOffset = false
        while (parentElement) {
            if (parentElement.classList.contains(blockListenerPropagation)) {
                // We clicked on something the blocks the listener, most
                // notably the hover context menu
                return false
            }

            if (parentElement === this.offsetParent) {
                inOffset = true
            }
            parentElement = parentElement?.parentElement
        }

        return inOffset
    }

    /**
     * Converts window x,y to relative position compared to offsetParent's top left corner
     * @param clientX
     * @param clientY
     * @returns
     */
    #relativePosition(clientX: number, clientY: number) {
        return relativePositionToElement([clientX, clientY], this.offsetParent)
    }

    // Remove all listeners
    destroy() {
        this.eventHelper.destroy()
        this.#scrollLockUnsubscriber()
    }
}

/**
 * =========================================================================
 * 💃 Multi-Tap Handling
 * =========================================================================
 */

/**
 * The SelectionMultiTapHandler class is responsible for handling multi-tap events.
 * It tracks the position of taps and their timing to distinguish between single, double, and triple taps.
 */
export class SelectionMultiTapHandler {
    /**
     * Represents a pending multi-tap event.
     * This property stores the coordinates of the last tap and its type.
     * It is initially set to null, indicating no multi-tap event is currently pending.
     */
    #pending: { x: number; y: number; tap: MultiTap } | null = null

    /**
     * Is there a multi tap pending ? Perhaps 0, 1, 2, 3?
     */
    #timeout: NodeJS.Timeout | number = 0

    /**
     * This method is called whenever a tap event occurs.
     * It determines whether the event is a single, double, or triple tap based on the timing and position of the tap.
     */
    processMultiTapEvent(x: number, y: number): { x: number; y: number; tap: MultiTap } {
        if (this.#pending === null) {
            // Set timer for multi-tap
            return this.#tap(x, y, MultiTap.SINGLE)
        } else {
            // The timeout hasn't finished, which means there may be a multi-tap available. Let's check the distances first.
            if (this.#checkDistance(this.#pending.x, this.#pending.y, x, y)) {
                return this.#tap(x, y, this.#incrementTap(this.#pending.tap))
            } else {
                // Distance is not up to par, so this will be treated as a regular tap
                return this.#tap(x, y, MultiTap.SINGLE)
            }
        }
    }
    /**
     * Increments the tap type from "single" to "double", from "double" to "triple",
     * and from "triple" back to "single".
     */
    #incrementTap(tap: MultiTap): MultiTap {
        switch (tap) {
            case MultiTap.SINGLE:
                return MultiTap.DOUBLE
            case MultiTap.DOUBLE:
                return MultiTap.TRIPLE
            case MultiTap.TRIPLE:
                return MultiTap.SINGLE
        }
    }

    /**
     * Checks if the distance between two points is within the allowed radius for a multi-tap event.
     * This method uses the Pythagorean theorem to calculate the distance between the two points.
     */
    #checkDistance(x1: number, y1: number, x2: number, y2: number) {
        return Math.hypot(x1 - x2, y1 - y2) < MULTI_TAP_DISTANCE_MIN
    }
    /**
     * Processes a tap event.
     * This method updates the `#multiTapPending` property with the coordinates and type of the tap.
     * It also sets a timer to reset the `#multiTapPending` property after a specific delay.
     */
    #tap(x: number, y: number, tap: MultiTap) {
        this.#pending = { x, y, tap }

        clearTimeout(this.#timeout)
        this.#timeout = setTimeout(() => {
            this.#pending = null
        }, MULTI_TAP_DELAY)
        return { ...this.#pending }
    }
}
